import { lookupPageSize } from '../utils/lookupTables';
import { setActiveTheme } from '../Hasp/properties/ConfigManager.js';
import { importFlows, resetFlows } from '../flow/useFlowStore.js';
import { useDatasetStore } from '../stores/datasetStore.js';
import { getObjectSchema, toWireKey } from '../Hasp/properties/v2/wireFormat.js';
import { restoreDesignerPageRoutingSettings } from '../draw/connectionRoutingSettings';
import { isProjectFontReference } from '../utils/fontReference.js';
import { restoreLayerLogic } from '../logic/pageLogic.js';
import { restoreLayerHass } from '../hass/pageHass.js';
import { migrateLayerHass } from '../hass/pageHassMigration.js';
import { migrateLayerActions } from '../logic/pageMigration.js';

export default class Importer {
    projectName = 'openhasp';

    /**
     * Reconstruct a Project Resource from a {"obj":"resource","kind":...}
     * line (see docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md):
     *   {"obj":"resource","kind":"dataset","id":44,"unit":"...","type":"...","series":[...]}
     * Fonts no longer have a resource line at all — a widget's Font Family
     * is just the literal `text_font` wire string (the selected project Font
     * Resource's `file` path), resolved lazily by the firmware
     * FontManager, mirroring how images are referenced directly by filename
     * with no registration step. See Exporter._processFonts().
     *
     * Widget lines never carry a `dataset` (Designer uuid) reference in
     * firmware-exported JSONL — Exporter._processDatasets() rewrites it,
     * since re-linking a widget back to the resource's uuid from just its
     * firmware-side numeric id is a separate, not-yet-implemented step. So
     * re-importing a firmware file picks the dataset back up into the
     * library, but any widget that used it won't be re-linked to it — an
     * accepted limitation of the current design, not a bug.
     */
    _importResourceLine(config) {
        if (config.kind === 'dataset') {
            useDatasetStore().importResource({
                id: config.id,
                unit: config.unit,
                type: config.type,
                series: config.series,
            });
        }
    }

    /**
     * Reverses Exporter._processFonts()'s two-key rewrite on a single widget
     * config line, restoring the Designer's own font<suffix>/text_font<suffix>
     * pair (family string + pixel size) before it reaches
     * mergeConfigWithTheme()/fromWireFormatStates() — otherwise the family
     * string ends up misread as the size and the Font Family selection
     * (font<suffix>) is simply gone, since _processFonts() deletes it.
     *
     * Keyed purely on the presence of a text_size(<suffix>) key: no schema
     * ever declares a real property by that name (see Exporter._processFonts's
     * doc comment — it's a pure export-time artifact), so this is always a
     * safe no-op for config that never went through that rewrite — a
     * design-save/undo-redo snapshot, which is exported without ever calling
     * _processFonts(), never carries one.
     */
    _unprocessFonts(config) {
        if (config.obj === 'btnmatrix') {
            if (config.text_size40 === undefined) return;
            config.font = isProjectFontReference(config.text_font40) ? config.text_font40 : '';
            config.text_font40 = config.text_size40;
            delete config.text_size40;
            return;
        }
        if (config.obj === 'chart' || config.obj === 'legend') {
            const schema = getObjectSchema(config.obj);
            const parts = config.obj === 'chart' ? ['x_axis', 'y_axis'] : ['label', 'value'];
            for (const part of parts) {
                const suffix = schema?.parts?.[part]?.exportSuffix ?? '';
                const sizeKey = 'text_size' + suffix;
                if (config[sizeKey] === undefined) continue;
                const fontKey = 'font' + suffix;
                const textFontKey = 'text_font' + suffix;
                config[fontKey] = isProjectFontReference(config[textFontKey]) ? config[textFontKey] : '';
                config[textFontKey] = config[sizeKey];
                delete config[sizeKey];
            }
        } else if (config.obj === 'badge' && config.text_size !== undefined) {
            config.text_font_resource = isProjectFontReference(config.text_font) ? config.text_font : '';
            config.text_font = config.text_size;
            delete config.text_size;
        } else if (config.text_size !== undefined) {
            config.font = isProjectFontReference(config.text_font) ? config.text_font : '';
            config.text_font = config.text_size;
            delete config.text_size;
        }

        if (config.value_size !== undefined) {
            config.value_font_resource = isProjectFontReference(config.value_font) ? config.value_font : '';
            config.value_font = config.value_size;
            delete config.value_size;
        }
    }

    /**
     * Handles a single design-metadata/page/resource/widget config object —
     * shared by doImport (design.json's `objects` array) and
     * doImportFromString (the undo/redo history's own internal snapshot
     * format, unrelated to the package format — see docs plan "Design
     * Packages", "Design.json format").
     *
     * `applyPlate` gates whether a `plate` metadata line overrides the
     * layer's resolution/theme. The currently selected plate always defines
     * page resolution (see App.vue's setPageSize()) — no imported file's
     * embedded plate config is ever allowed to override it. doImport()/
     * importParsedDesign() therefore always pass `false` here; only
     * doImportFromString() (restoring the *same* session's own undo/redo or
     * bookmark snapshot, never a foreign file) passes `true`.
     */
    _handleConfig(config, layer, applyPlate) {
        if (config.obj === 'badge') {
            if (config.icon !== undefined && config.text === undefined) config.text = config.icon;
            if (config.val !== undefined && config.value === undefined) config.value = config.val;
            delete config.icon;
            delete config.val;
        }
        if (config.obj === 'tabview') {
            config.width ??= layer.pageWidth;
            config.height ??= layer.pageHeight;
        }
        //this is the design metadata
        if (config.id === undefined && config.page === undefined) {
            // Restore script flows
            if (config.comment === '[flows]' && config.flows) {
                importFlows(config.flows);
                return;
            }
            if (config.plate !== undefined && applyPlate) {
                this.pageSize = config.plate;
                layer.pageWidth  = this.pageSize.w;
                layer.pageHeight = this.pageSize.h;
                // Initialise the active designer theme from the saved plate config
                setActiveTheme(
                    this.pageSize.theme,
                    this.pageSize.primary_color,
                    this.pageSize.secondary_color,
                );
            }
        } else if ((config.obj === undefined || config.obj === 'template_page' || config.isTemplate) && config.page !== undefined && config.id === undefined) {
            restoreDesignerPageRoutingSettings(layer, config);
            const isTemplate = config.obj === 'template_page' || config.isTemplate;
            if (!layer.findPage(config.page)) {
                const pageconfig = {
                    page:        config.page,
                    obj:         isTemplate ? 'template_page' : 'page',
                    haspid:      config.page,
                    width:       this.pageWidth,
                    height:      this.pageHeight,
                    // Preserve explicit page background styles from the
                    // design/export record. Older records omit these fields;
                    // keep the plate default as their fallback. bg_opa must
                    // travel alongside bg_color for translucent hex8 colors.
                    bg_color:    config.bg_color ?? this.pageColor,
                    bg_opa:      config.bg_opa,
                    comment:     config.comment,
                    swipe:       config.swipe,
                    transition:  config.transition,
                    transition_direction: config.transition_direction,
                    transition_duration: config.transition_duration,
                    targetPage:  config.targetPage ?? null,
                    underlayPage: config.underlayPage ?? null,
                };
                // Page style fields arrive in the same flat wire-keyed form
                // emitted by HaspPage.export(). Copy the fields declared by
                // the V2 page schema instead of maintaining a page-specific
                // whitelist here; otherwise new supported styles (including
                // background gradients) are silently discarded before
                // mergeConfigWithTheme can normalize them into parts.main.
                const pageSchema = getObjectSchema(pageconfig.obj);
                for (const [partName, partDef] of Object.entries(pageSchema?.parts ?? {})) {
                    for (const propName of Object.keys(partDef.properties ?? {})) {
                        const wireKey = toWireKey(partName, propName, partDef);
                        if (config[wireKey] !== undefined) pageconfig[wireKey] = config[wireKey];
                    }
                }
                if (config.x !== undefined && config.y !== undefined) {
                    pageconfig.x = config.x;
                    pageconfig.y = config.y;
                }
                layer.addPage(pageconfig);
            }
            // Resolved once all objects exist (restoreLayerLogic).
            if (config.logic !== undefined) {
                const page = layer.findPage(config.page);
                if (page) page._pendingLogic = config.logic;
            }
            // Resolved once all objects exist (restoreLayerHass).
            if (config.hass !== undefined) {
                const page = layer.findPage(config.page);
                if (page) page._pendingHass = config.hass;
            }
        } else if (config.obj === 'resource') {
            this._importResourceLine(config);
        } else {
            if (config.id === 0 && config.page !== undefined) {
                const page = layer.findPage(config.page);
                if (page) {
                    if (config.prev) page.addPageNav('prev', config.prev);
                    if (config.next) page.addPageNav('next', config.next);
                    if (config.back) page.addPageNav('back', config.back);
                }
            } else {
                this._unprocessFonts(config);
                layer.importObject(config);
            }
        }
    }

    /**
     * Imports an already-parsed design.json object — `{ ..., objects: [...] }`,
     * where `objects` is the same array Exporter.doExport() already produces.
     * The one place doImport() and DesignPackageLoader (which already has the
     * parsed object in hand, no File to re-read) both funnel through — so
     * this is the single source of truth for "never apply an imported
     * design's own plate config" (see _handleConfig()'s comment); no caller
     * can special-case its way back into applying it.
     */
    async importParsedDesign(parsed, layer, callback) {
        this.layer = layer;
        const objects = Array.isArray(parsed?.objects) ? parsed.objects : [];

        layer.clearAll();
        resetFlows();

        for (let i = 0; i < objects.length; i++) {
            await callback({
                progress: (i + 1) / objects.length,
                message: `Processing object ${i + 1} of ${objects.length}`
            });
            this._handleConfig(objects[i], layer, false);
        }
        restoreLayerLogic(layer).forEach(message => console.warn(message));
        restoreLayerHass(layer).forEach(message => console.warn(message));
        // Stored per-object actions (older designs, device JSONL) become event/action nodes.
        migrateLayerActions(layer);
        // Stored per-object Home Assistant configuration (HASSBuilder) becomes flow nodes.
        migrateLayerHass(layer);

        await callback({ progress: 1, message: 'Import complete!' });
    }

    /**
     * Imports a package's design.json from a File/Blob — a plain JSON
     * document, not JSONL. Legacy openHASP/Designer JSONL goes through
     * LegacyPlateImporter instead (see src/package/LegacyPlateImporter.js).
     */
    async doImport(file, layer, callback) {
        try {
            const text = await file.text();
            const parsed = JSON.parse(text);
            await this.importParsedDesign(parsed, layer, callback);
        } catch (error) {
            console.error("An error occurred during the import process:", error);
        }
    };

    /**
     * Restores an undo/redo history snapshot — a private, never-persisted
     * JSONL string internal to the history system, unrelated to the
     * design.json package format doImport() reads.
     */
    async doImportFromString(str, layer, callback) {
        this.layer = layer;
        const lines = str.split('\n');
        layer.clearAll();
        resetFlows();
        for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
            const lineContent = lines[lineIndex];
            if (lineContent.length < 4) continue;
            await callback({
                progress: (lineIndex + 1) / lines.length,
                message: `Processing line ${lineIndex + 1} of ${lines.length}`
            });
            let config;
            try {
                config = JSON.parse(lineContent);
            } catch (e) {
                continue;
            }
            this._handleConfig(config, layer, true);
        }
        restoreLayerLogic(layer).forEach(message => console.warn(message));
        restoreLayerHass(layer).forEach(message => console.warn(message));
        // Stored per-object actions (older designs, device JSONL) become event/action nodes.
        migrateLayerActions(layer);
        // Stored per-object Home Assistant configuration (HASSBuilder) becomes flow nodes.
        migrateLayerHass(layer);
        await callback({ progress: 1, message: 'Import complete!' });
    };


}
